import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClientConfiguration } from "../../models/ClientConfiguration/ClientConfiguration";
import ClientConfigurationsRepository from "../../repositories/ClientConfigurationsRepository";
import ClientInfoRepository from "../../repositories/ClientInfoRepository";
import { QUERY_KEYS } from "../../constants/QueryKeys";

interface DeleteClientConfigurationModalProps {
  open: boolean;
  config: ClientConfiguration | null;
  organizationId: string;
  onClose: () => void;
  onConfirm: (replacementConfigId: string | null, newDefaultConfigId: string | null) => Promise<void>;
}

const UNASSIGN_VALUE = "__unassign__";

export const DeleteClientConfigurationModal: React.FC<DeleteClientConfigurationModalProps> = ({
  open,
  config,
  organizationId,
  onClose,
  onConfirm,
}) => {
  const [replacementConfigId, setReplacementConfigId] = useState<string | null>(null);
  const [newDefaultConfigId, setNewDefaultConfigId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasAssignedClients = (config?.assignedClientsCount ?? 0) > 0;
  const isDefault = config?.isDefault ?? false;

  const clientConfigurationsRepository = new ClientConfigurationsRepository();
  const { data: configs = [] } = useQuery({
    queryKey: [QUERY_KEYS.CLIENT_CONFIGURATIONS, organizationId],
    queryFn: () => clientConfigurationsRepository.getClientConfigurations(organizationId),
    staleTime: 1000 * 60 * 5,
    enabled: open && (hasAssignedClients || isDefault),
  });

  const clientInfoRepository = new ClientInfoRepository();
  const { data: assignedClients = [] } = useQuery({
    queryKey: [QUERY_KEYS.CLIENT_INFOS, organizationId, config?.id],
    queryFn: () => clientInfoRepository.getClientInfos(organizationId, config!.id!),
    staleTime: 1000 * 60 * 5,
    enabled: open && hasAssignedClients && !!config?.id,
  });

  const otherConfigs = configs.filter((c) => c.id !== config?.id);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(
        hasAssignedClients ? replacementConfigId : null,
        isDefault ? newDefaultConfigId : null,
      );
    } finally {
      setIsSubmitting(false);
      setReplacementConfigId(null);
      setNewDefaultConfigId(null);
    }
  };

  const handleClose = () => {
    setReplacementConfigId(null);
    setNewDefaultConfigId(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent role="alertdialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-amber-500" />
            Confirmation required
          </DialogTitle>
        </DialogHeader>

        <Separator />

        <p className="text-sm text-foreground">Are you sure you want to delete this configuration?</p>

        <div className="flex flex-wrap">
          <Badge variant="outline-primary">{config?.name}</Badge>
        </div>

        {hasAssignedClients && (
          <div className="mt-2 flex flex-col gap-3">
            <p className="flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400">
              <AlertTriangle className="size-4 shrink-0" />
              {`This configuration is assigned to ${config!.assignedClientsCount} ${config!.assignedClientsCount === 1 ? "client" : "clients"}:`}
            </p>
            <div className="flex flex-wrap gap-1 pl-1">
              {assignedClients.map((client) => (
                <Badge key={client.id} variant="neutral" className="text-xs">
                  {client.id}
                </Badge>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label>Reassign clients to</Label>
              <div className="flex items-center gap-1">
                <Select
                  value={replacementConfigId ?? UNASSIGN_VALUE}
                  onValueChange={(value) =>
                    setReplacementConfigId(value === UNASSIGN_VALUE ? null : value)
                  }
                >
                  <SelectTrigger size="sm" className="w-full">
                    <SelectValue placeholder="Unassign (no configuration)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGN_VALUE}>Unassign (no configuration)</SelectItem>
                    {otherConfigs.map((c) => (
                      <SelectItem key={c.id} value={c.id!}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {replacementConfigId && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0"
                    onClick={() => setReplacementConfigId(null)}
                  >
                    <X className="size-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {isDefault && (
          <div className="mt-2 flex flex-col gap-3">
            <p className="flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400">
              <AlertTriangle className="size-4 shrink-0" />
              This is the default configuration. You must select a new default before deleting.
            </p>
            <div className="space-y-1.5">
              <Label>New default configuration</Label>
              <Select
                value={newDefaultConfigId ?? ""}
                onValueChange={(value) => setNewDefaultConfigId(value)}
              >
                <SelectTrigger size="sm" className="w-full">
                  <SelectValue placeholder="Select new default configuration" />
                </SelectTrigger>
                <SelectContent>
                  {otherConfigs.map((c) => (
                    <SelectItem key={c.id} value={c.id!}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <Separator />

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="min-w-[80px]"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isSubmitting || (isDefault && newDefaultConfigId === null)}
            className="min-w-[80px]"
          >
            {isSubmitting && <Loader2 className="size-4 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteClientConfigurationModal;
